import {createApiFetcher, createQueryString} from "@variocube/app-ui";
import {createApiClient as createGeneratedApiClient, EndpointParameters, Method} from "./client.gen";

// Headers that must never be set from the typed client — they belong to the transport layer (the
// fetcher injects auth) or to the server (Cookie). Anything else declared as a header parameter in
// the OpenAPI spec passes through. A denylist instead of an allow-list means a new benign header
// (e.g. X-Locale) added to the spec "just works" without touching this file, while the actual
// abuse pattern the original review flagged — smuggling Authorization / Cookie / tenant headers —
// fails loudly.
const SENSITIVE_REQUEST_HEADERS = new Set(
	["authorization", "cookie", "x-tenant-id", "x-forwarded-for"],
);

function getApiBaseUrl() {
	const apiParam = new URLSearchParams(location.search).get("api");
	if (apiParam) {
		return apiParam;
	}
	switch (location.hostname) {
		case "booking-dev.safecube.io":
			return "https://admin-dev.safecube.io";
		case "localhost":
		case "127.0.0.1":
			return "http://localhost:52000";
		default:
			return "https://admin.safecube.io";
	}
}
const baseUrl = getApiBaseUrl();

export function createApiClient(options: { accessToken?: string } = {}) {
	const {fetch} = createApiFetcher(baseUrl);
	const {accessToken} = options;

	return createGeneratedApiClient(async (method: Method, url: string, params: EndpointParameters | undefined) => {
		// build query string
		const qs = params?.query ? "?" + createQueryString(params.query) : "";

		// resolve path variables
		const resolvedUrl = url.replaceAll(/{[a-zA-Z]+}/g, (pathVariable) => {
			const pathVariableName = pathVariable.substring(1, pathVariable.length - 1);
			const value = params?.path && params?.path[pathVariableName];
			if (!value) {
				throw new Error(`Missing path variable ${pathVariableName}.`);
			}
			return encodeURIComponent(`${value}`);
		});

		const headers: Record<string, string> = {};
		if (params?.body) {
			headers["Content-Type"] = "application/json";
		}
		if (params?.header) {
			for (const [key, value] of Object.entries(params.header)) {
				if (SENSITIVE_REQUEST_HEADERS.has(key.toLowerCase())) {
					throw new Error(`Header ${key} cannot be set from the typed client.`);
				}
				if (value !== undefined && value !== null) {
					headers[key] = String(value);
				}
			}
		}
		if (accessToken) {
			headers["Authorization"] = `Bearer ${accessToken}`;
		}

		const response = await fetch(resolvedUrl + qs, {
			method,
			body: params?.body ? JSON.stringify(params.body) : undefined,
			headers,
		});

		const contentType = response.headers.get("content-type");

		if (contentType && /application\/.*json/.test(contentType)) {
			return await response.json();
		}
	});
}

export function getBaseUrl() {
	return baseUrl;
}

export * from "./client.gen";
